/**
 * multiTenantStorage.ts
 * マルチテナント対応ストレージ管理システム
 *
 * 機能:
 * - テナント分離されたStorageプリフィックス管理
 * - クロステナント分離の強制保証
 * - パフォーマンス最適化（<10ms目標）
 * - 既存crypto.tsとの100%後方互換性
 * - セキュリティ監査ログ連携準備
 */

import { StorageConfig, DeviceCharacteristics } from './crypto';

// ========================================
// 型定義 - Multi-Tenant Storage
// ========================================

export interface TenantStorageConfig {
  tenantId: string;
  isolation: 'strict' | 'flexible';
  encryptionLevel: 'standard' | 'enterprise';
  auditEnabled: boolean;
  storageQuota?: number; // bytes
  dataRetention?: number; // days
}

export interface TenantContext {
  tenantId: string;
  userId: string;
  deviceFingerprint?: string;
  sessionId?: string;
  accessLevel: 'read' | 'write' | 'admin';
}

export interface StorageOperation {
  operation: 'read' | 'write' | 'delete' | 'list';
  key: string;
  tenantId: string;
  userId: string;
  timestamp: number;
  deviceFingerprint?: string;
  dataSize?: number;
  success: boolean;
  error?: string;
}

export interface TenantStorageStats {
  tenantId: string;
  totalKeys: number;
  totalSize: number; // bytes
  lastActivity: number;
  operationCounts: {
    read: number;
    write: number;
    delete: number;
  };
  quotaUsage: number; // percentage
}

export interface CrossTenantViolation {
  violationType: 'unauthorized_access' | 'key_collision' | 'data_leak';
  sourceTenatId: string;
  targetTenantId: string;
  attemptedKey: string;
  userId: string;
  deviceFingerprint?: string;
  timestamp: number;
  blocked: boolean;
}

// ========================================
// Storage Keys - Tenant Prefixes
// ========================================

export const TENANT_STORAGE_PREFIXES = {
  // JWT関連
  JWT_ACCESS: (tenantId: string) => `ripia_t${tenantId}_jwt_access`,
  JWT_REFRESH: (tenantId: string) => `ripia_t${tenantId}_jwt_refresh`,
  JWT_PAIR: (tenantId: string) => `ripia_t${tenantId}_jwt_pair`,
  JWT_BLACKLIST: (tenantId: string) => `ripia_t${tenantId}_jwt_blacklist`,

  // セッション・デバイス関連
  SESSION: (tenantId: string) => `ripia_t${tenantId}_session`,
  DEVICE: (tenantId: string) => `ripia_t${tenantId}_device`,
  FINGERPRINT: (tenantId: string) => `ripia_t${tenantId}_fingerprint`,

  // ユーザーデータ関連
  USER_DATA: (tenantId: string) => `ripia_t${tenantId}_user_data`,
  USER_SETTINGS: (tenantId: string) => `ripia_t${tenantId}_user_settings`,
  USER_CACHE: (tenantId: string) => `ripia_t${tenantId}_user_cache`,

  // テナント設定関連
  TENANT_CONFIG: (tenantId: string) => `ripia_t${tenantId}_config`,
  TENANT_AUDIT: (tenantId: string) => `ripia_t${tenantId}_audit`,
  TENANT_STATS: (tenantId: string) => `ripia_t${tenantId}_stats`,

  // グローバル設定（全テナント共有）
  GLOBAL_CONFIG: 'ripia_global_config',
  GLOBAL_DEVICE_REGISTRY: 'ripia_global_device_registry',
  GLOBAL_SECURITY_LOG: 'ripia_global_security_log',
} as const;

// ========================================
// Multi-Tenant Storage Service
// ========================================

export class MultiTenantStorageService {
  private static readonly ISOLATION_CHECK_CACHE = new Map<string, boolean>();
  private static readonly OPERATION_LOG: StorageOperation[] = [];
  private static readonly VIOLATION_LOG: CrossTenantViolation[] = [];
  private static readonly TENANT_STATS = new Map<string, TenantStorageStats>();

  // ========================================
  // Core Storage Operations with Tenant Isolation
  // ========================================

  /**
   * テナント分離されたセキュアget操作
   * パフォーマンス目標: <10ms
   */
  static async getTenantData<T>(
    context: TenantContext,
    key: string,
    category: keyof typeof TENANT_STORAGE_PREFIXES
  ): Promise<T | null> {
    const startTime = performance.now();

    try {
      // 1. テナント分離チェック
      const isolatedKey = this.buildTenantKey(context.tenantId, key, category);
      if (!this.validateTenantAccess(context, isolatedKey)) {
        this.logViolation({
          violationType: 'unauthorized_access',
          sourceTenatId: context.tenantId,
          targetTenantId: 'unknown',
          attemptedKey: key,
          userId: context.userId,
          deviceFingerprint: context.deviceFingerprint,
          timestamp: Date.now(),
          blocked: true,
        });
        return null;
      }

      // 2. データ取得
      const rawData = localStorage.getItem(isolatedKey);
      if (!rawData) {
        this.logOperation({
          operation: 'read',
          key: isolatedKey,
          tenantId: context.tenantId,
          userId: context.userId,
          timestamp: Date.now(),
          deviceFingerprint: context.deviceFingerprint,
          success: true,
        });
        return null;
      }

      // 3. 復号化（existing crypto.tsを活用）
      const config: StorageConfig = {
        encrypt: true,
        compress: true,
        validateIntegrity: true,
      };

      // crypto.tsの既存関数を使用（実装時に適切な復号化関数を呼び出し）
      const decryptedData = this.decryptWithTenantKey(
        rawData,
        context.tenantId,
        config
      );
      const parsedData = JSON.parse(decryptedData) as T;

      // 4. パフォーマンス測定
      const executionTime = performance.now() - startTime;
      if (executionTime > 10) {
        console.warn(
          `[MultiTenantStorage] Slow read operation: ${executionTime.toFixed(2)}ms for key ${isolatedKey}`
        );
      }

      // 5. 統計更新
      this.updateTenantStats(context.tenantId, 'read', rawData.length);

      this.logOperation({
        operation: 'read',
        key: isolatedKey,
        tenantId: context.tenantId,
        userId: context.userId,
        timestamp: Date.now(),
        deviceFingerprint: context.deviceFingerprint,
        dataSize: rawData.length,
        success: true,
      });

      return parsedData;
    } catch (error) {
      this.logOperation({
        operation: 'read',
        key: key,
        tenantId: context.tenantId,
        userId: context.userId,
        timestamp: Date.now(),
        deviceFingerprint: context.deviceFingerprint,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return null;
    }
  }

  /**
   * テナント分離されたセキュアset操作
   * パフォーマンス目標: <15ms
   */
  static async setTenantData<T>(
    context: TenantContext,
    key: string,
    data: T,
    category: keyof typeof TENANT_STORAGE_PREFIXES,
    config: Partial<StorageConfig> = {}
  ): Promise<boolean> {
    const startTime = performance.now();

    try {
      // 1. アクセス権チェック
      if (context.accessLevel === 'read') {
        throw new Error('Insufficient permissions for write operation');
      }

      // 2. テナント分離キー生成
      const isolatedKey = this.buildTenantKey(context.tenantId, key, category);

      // 3. クォータチェック
      const serializedData = JSON.stringify(data);
      if (!this.checkStorageQuota(context.tenantId, serializedData.length)) {
        throw new Error('Storage quota exceeded');
      }

      // 4. 暗号化（existing crypto.tsを活用）
      const storageConfig: StorageConfig = {
        encrypt: true,
        compress: config.compress ?? true,
        validateIntegrity: true,
        ...config,
      };

      const encryptedData = this.encryptWithTenantKey(
        serializedData,
        context.tenantId,
        storageConfig
      );

      // 5. ストレージ書き込み
      localStorage.setItem(isolatedKey, encryptedData);

      // 6. パフォーマンス測定
      const executionTime = performance.now() - startTime;
      if (executionTime > 15) {
        console.warn(
          `[MultiTenantStorage] Slow write operation: ${executionTime.toFixed(2)}ms for key ${isolatedKey}`
        );
      }

      // 7. 統計更新
      this.updateTenantStats(context.tenantId, 'write', encryptedData.length);

      this.logOperation({
        operation: 'write',
        key: isolatedKey,
        tenantId: context.tenantId,
        userId: context.userId,
        timestamp: Date.now(),
        deviceFingerprint: context.deviceFingerprint,
        dataSize: encryptedData.length,
        success: true,
      });

      return true;
    } catch (error) {
      this.logOperation({
        operation: 'write',
        key: key,
        tenantId: context.tenantId,
        userId: context.userId,
        timestamp: Date.now(),
        deviceFingerprint: context.deviceFingerprint,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return false;
    }
  }

  /**
   * テナント分離されたセキュア削除操作
   */
  static async deleteTenantData(
    context: TenantContext,
    key: string,
    category: keyof typeof TENANT_STORAGE_PREFIXES
  ): Promise<boolean> {
    try {
      if (context.accessLevel !== 'admin' && context.accessLevel !== 'write') {
        throw new Error('Insufficient permissions for delete operation');
      }

      const isolatedKey = this.buildTenantKey(context.tenantId, key, category);

      if (!this.validateTenantAccess(context, isolatedKey)) {
        this.logViolation({
          violationType: 'unauthorized_access',
          sourceTenatId: context.tenantId,
          targetTenantId: 'unknown',
          attemptedKey: key,
          userId: context.userId,
          deviceFingerprint: context.deviceFingerprint,
          timestamp: Date.now(),
          blocked: true,
        });
        return false;
      }

      // セキュア削除実行
      localStorage.removeItem(isolatedKey);

      // 統計更新
      this.updateTenantStats(context.tenantId, 'delete', 0);

      this.logOperation({
        operation: 'delete',
        key: isolatedKey,
        tenantId: context.tenantId,
        userId: context.userId,
        timestamp: Date.now(),
        deviceFingerprint: context.deviceFingerprint,
        success: true,
      });

      return true;
    } catch (error) {
      this.logOperation({
        operation: 'delete',
        key: key,
        tenantId: context.tenantId,
        userId: context.userId,
        timestamp: Date.now(),
        deviceFingerprint: context.deviceFingerprint,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return false;
    }
  }

  // ========================================
  // Tenant Management & Security
  // ========================================

  /**
   * テナント全データの一括取得（管理者用）
   */
  static async getAllTenantData(
    context: TenantContext
  ): Promise<Record<string, unknown> | null> {
    if (context.accessLevel !== 'admin') {
      throw new Error('Admin access required for tenant data listing');
    }

    const tenantPrefix = `ripia_t${context.tenantId}_`;
    const tenantData: Record<string, unknown> = {};

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(tenantPrefix)) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const decryptedValue = this.decryptWithTenantKey(
              value,
              context.tenantId,
              {
                encrypt: true,
                compress: true,
                validateIntegrity: true,
              }
            );
            tenantData[key.replace(tenantPrefix, '')] =
              JSON.parse(decryptedValue);
          } catch (error) {
            // 復号化に失敗したキーは警告を出力してスキップ
            console.warn(
              `[MultiTenantStorage] Failed to decrypt key: ${key}`,
              error
            );
          }
        }
      }
    }

    return tenantData;
  }

  /**
   * テナント完全削除（GDPR対応）
   */
  static async purgeTenantData(
    context: TenantContext,
    confirmationToken: string
  ): Promise<{ success: boolean; deletedKeys: string[]; errors: string[] }> {
    if (context.accessLevel !== 'admin') {
      throw new Error('Admin access required for tenant purge');
    }

    // 確認トークンの検証（実装時に適切な検証ロジックを追加）
    if (!this.validatePurgeToken(context.tenantId, confirmationToken)) {
      throw new Error('Invalid confirmation token for tenant purge');
    }

    const deletedKeys: string[] = [];
    const errors: string[] = [];
    const tenantPrefix = `ripia_t${context.tenantId}_`;

    // テナント関連キーを特定
    const keysToDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(tenantPrefix)) {
        keysToDelete.push(key);
      }
    }

    // セキュア削除実行
    for (const key of keysToDelete) {
      try {
        localStorage.removeItem(key);
        deletedKeys.push(key);

        // セキュア削除ログ
        this.logOperation({
          operation: 'delete',
          key: key,
          tenantId: context.tenantId,
          userId: context.userId,
          timestamp: Date.now(),
          deviceFingerprint: context.deviceFingerprint,
          success: true,
        });
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : 'Unknown deletion error';
        errors.push(`${key}: ${errorMsg}`);
      }
    }

    // テナント統計削除
    this.TENANT_STATS.delete(context.tenantId);

    return {
      success: errors.length === 0,
      deletedKeys,
      errors,
    };
  }

  // ========================================
  // Security & Validation
  // ========================================

  /**
   * テナントキー生成（セキュア）
   */
  private static buildTenantKey(
    tenantId: string,
    key: string,
    category: keyof typeof TENANT_STORAGE_PREFIXES
  ): string {
    // テナントIDの妥当性検証
    if (!this.validateTenantId(tenantId)) {
      throw new Error(`Invalid tenant ID: ${tenantId}`);
    }

    // キー名の妥当性検証
    if (!this.validateKeyName(key)) {
      throw new Error(`Invalid key name: ${key}`);
    }

    const prefix = TENANT_STORAGE_PREFIXES[category];
    if (typeof prefix === 'function') {
      return `${prefix(tenantId)}_${key}`;
    } else {
      // グローバルキーの場合
      return `${prefix}_${key}`;
    }
  }

  /**
   * テナントアクセス権限検証
   */
  private static validateTenantAccess(
    context: TenantContext,
    isolatedKey: string
  ): boolean {
    const cacheKey = `${context.tenantId}_${context.userId}_${isolatedKey}`;

    // キャッシュチェック（パフォーマンス最適化）
    if (this.ISOLATION_CHECK_CACHE.has(cacheKey)) {
      return this.ISOLATION_CHECK_CACHE.get(cacheKey)!;
    }

    // テナントプリフィックスの検証
    const expectedPrefix = `ripia_t${context.tenantId}_`;
    const isValid = isolatedKey.startsWith(expectedPrefix);

    // キャッシュに保存（メモリリーク防止のため最大1000エントリ）
    if (this.ISOLATION_CHECK_CACHE.size >= 1000) {
      const firstKey = this.ISOLATION_CHECK_CACHE.keys().next().value;
      if (firstKey) {
        this.ISOLATION_CHECK_CACHE.delete(firstKey);
      }
    }
    this.ISOLATION_CHECK_CACHE.set(cacheKey, isValid);

    return isValid;
  }

  /**
   * クロステナント違反ログ記録
   */
  private static logViolation(violation: CrossTenantViolation): void {
    this.VIOLATION_LOG.push(violation);

    // メモリリーク防止：最新1000件のみ保持
    if (this.VIOLATION_LOG.length > 1000) {
      this.VIOLATION_LOG.splice(0, this.VIOLATION_LOG.length - 1000);
    }

    // 重要な違反は即座にコンソール出力
    if (
      violation.violationType === 'data_leak' ||
      violation.violationType === 'unauthorized_access'
    ) {
      console.error('[SECURITY VIOLATION]', violation);
    }
  }

  /**
   * 操作ログ記録
   */
  private static logOperation(operation: StorageOperation): void {
    this.OPERATION_LOG.push(operation);

    // メモリリーク防止：最新5000件のみ保持
    if (this.OPERATION_LOG.length > 5000) {
      this.OPERATION_LOG.splice(0, this.OPERATION_LOG.length - 5000);
    }
  }

  // ========================================
  // Statistics & Monitoring
  // ========================================

  /**
   * テナント使用統計更新
   */
  private static updateTenantStats(
    tenantId: string,
    operation: 'read' | 'write' | 'delete',
    dataSize: number
  ): void {
    if (!this.TENANT_STATS.has(tenantId)) {
      this.TENANT_STATS.set(tenantId, {
        tenantId,
        totalKeys: 0,
        totalSize: 0,
        lastActivity: Date.now(),
        operationCounts: { read: 0, write: 0, delete: 0 },
        quotaUsage: 0,
      });
    }

    const stats = this.TENANT_STATS.get(tenantId)!;
    stats.operationCounts[operation]++;
    stats.lastActivity = Date.now();

    if (operation === 'write') {
      stats.totalKeys++;
      stats.totalSize += dataSize;
    } else if (operation === 'delete') {
      stats.totalKeys = Math.max(0, stats.totalKeys - 1);
    }

    // クォータ使用率計算（デフォルト10MB）
    const quotaLimit = 10 * 1024 * 1024; // 10MB
    stats.quotaUsage = (stats.totalSize / quotaLimit) * 100;
  }

  /**
   * テナント統計取得
   */
  static getTenantStats(tenantId: string): TenantStorageStats | null {
    return this.TENANT_STATS.get(tenantId) || null;
  }

  /**
   * 全テナント統計取得（システム管理者用）
   */
  static getAllTenantStats(): TenantStorageStats[] {
    return Array.from(this.TENANT_STATS.values());
  }

  /**
   * セキュリティ違反履歴取得
   */
  static getViolationHistory(tenantId?: string): CrossTenantViolation[] {
    if (tenantId) {
      return this.VIOLATION_LOG.filter(
        (v) => v.sourceTenatId === tenantId || v.targetTenantId === tenantId
      );
    }
    return [...this.VIOLATION_LOG];
  }

  // ========================================
  // Utility & Helper Methods
  // ========================================

  /**
   * ストレージクォータチェック
   */
  private static checkStorageQuota(
    tenantId: string,
    dataSize: number
  ): boolean {
    const stats = this.TENANT_STATS.get(tenantId);
    if (!stats) return true; // 初回書き込みは許可

    const quotaLimit = 10 * 1024 * 1024; // 10MB デフォルト
    return stats.totalSize + dataSize <= quotaLimit;
  }

  /**
   * テナントID妥当性検証
   */
  private static validateTenantId(tenantId: string): boolean {
    // 英数字とハイフンのみ、3-50文字
    return /^[a-zA-Z0-9-]{3,50}$/.test(tenantId);
  }

  /**
   * キー名妥当性検証
   */
  private static validateKeyName(key: string): boolean {
    // 英数字、アンダースコア、ハイフンのみ、1-100文字
    return /^[a-zA-Z0-9_-]{1,100}$/.test(key);
  }

  /**
   * パージトークン検証（実装時に強化）
   */
  private static validatePurgeToken(tenantId: string, token: string): boolean {
    // TODO: 実際のプロジェクトでは暗号学的に安全なトークン検証を実装
    return token === `PURGE_${tenantId}_${Date.now().toString().slice(-6)}`;
  }

  /**
   * テナントキー暗号化（crypto.ts統合）
   */
  private static encryptWithTenantKey(
    data: string,
    tenantId: string,
    _config: StorageConfig
  ): string {
    // TODO: crypto.tsの既存暗号化関数を呼び出し
    // 実装時にはcrypto.tsのencrypt関数を使用
    // テナント固有のキーデリベーションを追加

    // モック実装（実際の実装時はcrypto.tsを使用）
    const tenantSalt = `ripia_tenant_${tenantId}`;
    return btoa(`${tenantSalt}:${data}`);
  }

  /**
   * テナントキー復号化（crypto.ts統合）
   */
  private static decryptWithTenantKey(
    encryptedData: string,
    tenantId: string,
    _config: StorageConfig
  ): string {
    // TODO: crypto.tsの既存復号化関数を呼び出し
    // 実装時にはcrypto.tsのdecrypt関数を使用
    // テナント固有のキーデリベーションを使用

    // モック実装（実際の実装時はcrypto.tsを使用）
    const decoded = atob(encryptedData);
    const tenantSalt = `ripia_tenant_${tenantId}:`;
    if (!decoded.startsWith(tenantSalt)) {
      throw new Error('Invalid tenant data or corrupted encryption');
    }
    return decoded.replace(tenantSalt, '');
  }
}

// ========================================
// Backward Compatibility Layer
// ========================================

/**
 * 既存crypto.ts関数との後方互換性
 * 既存のsecureStoreData, secureGetData関数をラップしてマルチテナント対応
 */
export class TenantCompatibilityLayer {
  /**
   * 既存secureStoreData関数をテナント対応でラップ
   */
  static async secureTenantStore<T>(
    key: string,
    data: T,
    tenantContext: TenantContext,
    config: Partial<StorageConfig> = {}
  ): Promise<boolean> {
    return MultiTenantStorageService.setTenantData(
      tenantContext,
      key,
      data,
      'USER_DATA',
      config
    );
  }

  /**
   * 既存secureGetData関数をテナント対応でラップ
   */
  static async secureTenantGet<T>(
    key: string,
    tenantContext: TenantContext
  ): Promise<T | null> {
    return MultiTenantStorageService.getTenantData<T>(
      tenantContext,
      key,
      'USER_DATA'
    );
  }

  /**
   * JWT系の既存関数をテナント対応でラップ
   */
  static async storeTenantJWT(
    tokenPair: { accessToken: string; refreshToken: string },
    tenantContext: TenantContext
  ): Promise<boolean> {
    const success = await MultiTenantStorageService.setTenantData(
      tenantContext,
      'current_jwt_pair',
      tokenPair,
      'JWT_PAIR'
    );

    if (success) {
      // 個別トークンも保存（既存コードとの互換性）
      await MultiTenantStorageService.setTenantData(
        tenantContext,
        'access_token',
        tokenPair.accessToken,
        'JWT_ACCESS'
      );
      await MultiTenantStorageService.setTenantData(
        tenantContext,
        'refresh_token',
        tokenPair.refreshToken,
        'JWT_REFRESH'
      );
    }

    return success;
  }

  /**
   * デバイスフィンガープリント保存
   */
  static async storeTenantDeviceFingerprint(
    fingerprint: string,
    characteristics: DeviceCharacteristics,
    tenantContext: TenantContext
  ): Promise<boolean> {
    const deviceData = {
      fingerprint,
      characteristics,
      lastUsed: Date.now(),
      trustScore: 100, // 初期信頼度
    };

    return MultiTenantStorageService.setTenantData(
      tenantContext,
      'device_fingerprint',
      deviceData,
      'FINGERPRINT'
    );
  }
}

// ========================================
// Developer Utilities
// ========================================

/**
 * 開発時デバッグ用ユーティリティ
 */
export class MultiTenantDebugUtils {
  /**
   * テナントストレージ内容をダンプ（開発環境のみ）
   */
  static dumpTenantStorage(tenantId: string): Record<string, unknown> {
    if (import.meta.env.PROD) {
      throw new Error('Debug utilities are not available in production');
    }

    const dump: Record<string, unknown> = {};
    const prefix = `ripia_t${tenantId}_`;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        dump[key] = localStorage.getItem(key);
      }
    }

    return dump;
  }

  /**
   * パフォーマンステスト実行
   */
  static async performanceTest(
    tenantId: string,
    operations: number = 1000
  ): Promise<{
    averageReadTime: number;
    averageWriteTime: number;
    operationsPerSecond: number;
  }> {
    const testContext: TenantContext = {
      tenantId,
      userId: 'test-user',
      accessLevel: 'admin',
    };

    let totalReadTime = 0;
    let totalWriteTime = 0;
    const testData = { test: 'performance', timestamp: Date.now() };

    console.log(
      `[MultiTenantStorage] Starting performance test: ${operations} operations`
    );

    // Write operations
    for (let i = 0; i < operations; i++) {
      const start = performance.now();
      await MultiTenantStorageService.setTenantData(
        testContext,
        `perf_test_${i}`,
        testData,
        'USER_DATA'
      );
      totalWriteTime += performance.now() - start;
    }

    // Read operations
    for (let i = 0; i < operations; i++) {
      const start = performance.now();
      await MultiTenantStorageService.getTenantData(
        testContext,
        `perf_test_${i}`,
        'USER_DATA'
      );
      totalReadTime += performance.now() - start;
    }

    // Cleanup
    for (let i = 0; i < operations; i++) {
      await MultiTenantStorageService.deleteTenantData(
        testContext,
        `perf_test_${i}`,
        'USER_DATA'
      );
    }

    return {
      averageReadTime: totalReadTime / operations,
      averageWriteTime: totalWriteTime / operations,
      operationsPerSecond:
        (operations * 2) / ((totalReadTime + totalWriteTime) / 1000),
    };
  }
}

export default MultiTenantStorageService;
